import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { endImpersonation } from '@/services/api.client';

export function ImpersonationBanner() {
  const { session, setSession } = useImpersonation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: endImpersonation,
    onSuccess: async () => {
      setSession(null);
      // Force useAuth() to refetch the super admin's own profile.
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      void navigate('/admin');
    },
    onError: () => {
      toast.error('Failed to end impersonation session. Please try again or log out.');
    },
  });

  if (!session) return null;

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-[var(--status-amber)] text-[var(--bg-page)] px-4 py-2 text-sm font-medium shrink-0"
    >
      <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1 min-w-0 break-words">
        You are impersonating <strong>{session.targetName}</strong> ({session.targetEmail}). All
        actions are audit-logged.
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-8 min-w-[6rem] text-xs border-[var(--bg-page)] text-[var(--bg-page)] hover:opacity-90"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
      >
        <X className="size-3 mr-1" aria-hidden="true" />
        End Session
      </Button>
    </div>
  );
}
